<!-- Mobile overlay -->
@if (mobile() && open()) {
  <div
    class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm xl:hidden"
    (click)="onClose.emit()"
  ></div>

  <aside class="app-sidebar-shell fixed inset-y-0 left-0 z-50 flex w-[300px] flex-col border-r border-gray-100 bg-white shadow-xl xl:hidden dark:border-[var(--border-primary)] dark:bg-[var(--bg-sidebar)] dark:shadow-[var(--shadow-lg)]">
    <ng-container *ngTemplateOutlet="sidebarContent"></ng-container>
  </aside>
}

<!-- Desktop sidebar -->
@if (!mobile()) {
  <aside
    [ngClass]="[
      'app-sidebar-shell relative hidden shrink-0 border-r border-gray-100 bg-white transition-all duration-300 xl:flex xl:flex-col dark:border-[var(--border-primary)] dark:bg-[var(--bg-sidebar)]',
      isDesktopCollapsed() ? 'xl:w-[80px]' : 'xl:w-[300px]'
    ]"
  >
    <!-- Toggle collapse button -->
    @if (!isModalOpen()) {
      <button
        (click)="onToggleCollapsed.emit()"
        [style.top.px]="72"
        class="absolute right-[-11px] z-50 flex size-[22px] translate-y-0 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm text-gray-500 transition-all hover:scale-110 hover:border-primary hover:text-black dark:border-[var(--border-secondary)] dark:bg-[var(--bg-elevated)] dark:text-[var(--text-primary)]"
      >
        <lucide-angular
          [img]="ChevronRight"
          [size]="11"
          [ngClass]="['transition-transform duration-300', !isDesktopCollapsed() ? 'rotate-180' : '']"
        />
      </button>
    }

    <ng-container *ngTemplateOutlet="sidebarContent"></ng-container>
  </aside>
}

<!-- ═══════════════════════════════════ Sidebar content template ═══ -->
<ng-template #sidebarContent>

  <!-- Logo -->
  <div
    [ngClass]="[
      'relative flex items-center',
      mobile()
        ? 'h-16 w-full justify-between border-b border-gray-100 px-6 dark:border-[var(--border-primary)]'
        : isDesktopCollapsed()
          ? 'mx-auto mt-5 flex h-10 w-full justify-center'
          : 'mx-auto mt-5 mb-7 flex min-h-12 w-[190px] items-center justify-center rounded-lg px-4'
    ]"
  >
    <div
      class="flex items-center justify-center"
      [ngClass]="mobile() ? 'h-12' : isDesktopCollapsed() ? 'size-8' : 'h-24 flex-1'"
    >
    <!-- Brand name or Logo -->
    @if (!isDesktopCollapsed() || mobile()) {
      @if (currentTenant()) {
        <app-tenant-logo
          [logoUrl]="currentTenant()?.logoUrl"
          [fallbackText]="currentTenant()?.displayName ?? ''"
          [imgClass]="isDesktopCollapsed() && !mobile() ? 'size-8' : 'h-full w-auto'"
          fallbackClass="max-w-full truncate px-2 text-center text-xl font-bold leading-none tracking-tight"
          [fallbackStyle]="themeService.isDark() ? 'color: var(--text-primary)' : 'color: #111827'"
        />
      } @else {
        <span [ngClass]="['max-w-full whitespace-nowrap text-center text-[clamp(24px,10vw,31px)] font-bold leading-none tracking-tight', themeService.isDark() ? 'text-[var(--text-primary)]' : 'text-gray-900']">
          Paybridge <span class="text-primary">Nexus</span>
        </span>
      }
    } @else {
      @if (currentTenant()) {
        <app-tenant-logo
          [logoUrl]="currentTenant()?.logoUrl"
          [fallbackText]="(currentTenant()?.displayName | slice:0:2) ?? ''"
          [imgClass]="isDesktopCollapsed() && !mobile() ? 'size-8' : 'h-full w-auto'"
          fallbackClass="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 p-1.5 text-[18px] font-bold uppercase leading-none text-primary"
        />
      } @else {
        <div class="inline-flex shrink-0 items-center justify-center rounded-[10px] bg-primary/10 p-1.5">
          <span class="text-[24px] font-bold leading-none text-primary">PN</span>
        </div>
      }
    }

    @if (mobile()) {
      <button (click)="onClose.emit()" class="absolute right-5 text-gray-400 hover:text-gray-700 dark:text-[var(--text-secondary)] dark:hover:text-[var(--text-primary)]">
        <lucide-angular [img]="X" [size]="20" />
      </button>
    }
    </div>
  </div>

  <!-- Tenant Selector -->
  <div [ngClass]="[isDesktopCollapsed() && !mobile() ? 'px-3 pt-4 pb-2' : 'px-4 pb-2']">
    <app-tenant-selector [collapsed]="isDesktopCollapsed() && !mobile()" />
  </div>

  <!-- Navigation -->
  <nav
    [ngClass]="[
      'app-sidebar-nav flex-1 overflow-y-auto pb-2 [scrollbar-gutter:stable]',
      isDesktopCollapsed() && !mobile() ? 'pl-4 pr-2' : 'px-3'
    ]"
  >
    <div class="flex flex-col gap-0">
      @for (item of navigation; track item.name) {
        @if (hasChildren(item)) {
          <!-- Parent item with children -->
          <div class="w-full">
            <button
              (click)="handleParentClick(item)"
              [attr.title]="isDesktopCollapsed() && !mobile() ? item.name : null"
              [ngClass]="itemClass(item, true)"
            >
              <lucide-angular
                [img]="item.icon"
                class="size-5 shrink-0"
                [strokeWidth]="isDesktopCollapsed() && !mobile() ? 2.75 : 2.25"
              />
              @if (!isDesktopCollapsed() || mobile()) {
                <span class="block min-w-0 flex-1 truncate text-left">{{ item.name }}</span>
                <lucide-angular
                  [img]="ChevronDown"
                  class="size-5 shrink-0 transition-transform duration-200"
                  [class.rotate-180]="isMenuOpen(item)"
                />
              }
            </button>

            @if ((!isDesktopCollapsed() || mobile()) && isMenuOpen(item)) {
              <div class="ml-[27px] border-l border-black/10 dark:border-[var(--border-secondary)]">
                @for (child of item.children; track child.name) {
                  @if (child.externalUrl) {
                    <a
                      [href]="child.externalUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      (click)="closeAfterExternalNavigation()"
                      [ngClass]="childLinkClass(child)"
                    >
                      <lucide-angular [img]="child.icon" class="size-4 shrink-0" [strokeWidth]="2.25" />
                      <span class="block min-w-0 flex-1 truncate">{{ child.name }}</span>
                    </a>
                  } @else {
                    <a
                      [routerLink]="child.href"
                      (click)="$event.preventDefault(); navigateTo(child)"
                      [ngClass]="childLinkClass(child)"
                    >
                      <lucide-angular [img]="child.icon" class="size-4 shrink-0" [strokeWidth]="2.25" />
                      <span class="block min-w-0 flex-1 truncate">{{ child.name }}</span>
                    </a>
                  }
                }
              </div>
            }
          </div>
        } @else {
          <!-- Leaf item -->
          @if (item.externalUrl) {
            <a
              [href]="item.externalUrl"
              target="_blank"
              rel="noopener noreferrer"
              (click)="closeAfterExternalNavigation()"
              [attr.title]="isDesktopCollapsed() && !mobile() ? item.name : null"
              [ngClass]="itemClass(item)"
            >
              <lucide-angular
                [img]="item.icon"
                class="size-5 shrink-0"
                [strokeWidth]="isDesktopCollapsed() && !mobile() ? 2.75 : 2.25"
              />
              @if (!isDesktopCollapsed() || mobile()) {
                <span class="block min-w-0 flex-1 truncate">{{ item.name }}</span>
              }
            </a>
          } @else {
            <a
              [routerLink]="item.href"
              (click)="$event.preventDefault(); navigateTo(item)"
              [attr.title]="isDesktopCollapsed() && !mobile() ? item.name : null"
              [ngClass]="itemClass(item)"
            >
              <lucide-angular
                [img]="item.icon"
                class="size-5 shrink-0"
                [strokeWidth]="isDesktopCollapsed() && !mobile() ? 2.75 : 2.25"
              />
              @if (!isDesktopCollapsed() || mobile()) {
                <span class="block min-w-0 flex-1 truncate">{{ item.name }}</span>
              }
            </a>
          }
        }
      }
    </div>
  </nav>

  <!-- Logout -->
  <div [ngClass]="[isDesktopCollapsed() && !mobile() ? 'px-3 pb-5 pt-2' : 'px-3 pb-5 pt-2']">
    <div class="app-sidebar-logout">
      <button
        (click)="onLogout.emit()"
        [attr.title]="isDesktopCollapsed() && !mobile() ? 'Cerrar sesión' : null"
        [ngClass]="logoutClass()"
      >
        <lucide-angular
          [img]="LogOut"
          class="size-5 shrink-0"
          [strokeWidth]="isDesktopCollapsed() && !mobile() ? 2.75 : 2.25"
        />
        @if (!isDesktopCollapsed() || mobile()) {
          <span class="block min-w-0 flex-1 truncate text-left">Cerrar sesión</span>
        }
      </button>
    </div>
  </div>

</ng-template>
